import React from "react";

export function inlineFormat(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**"))
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith("`") && part.endsWith("`"))
      return <code key={i} style={{ background:"rgba(0,0,0,0.06)", padding:"1px 5px", borderRadius:3, fontSize:"0.9em", fontFamily:"var(--font-mono)" }}>{part.slice(1,-1)}</code>;
    return part;
  });
}

export function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let i = 0;
  let numberedCounter = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      elements.push(<div key={`sp-${i}`} style={{ height: 6 }} />);
      i++;
      continue;
    }

    if (/^[-_*]{3,}\s*$/.test(line.trim())) {
      numberedCounter = 0;
      elements.push(<hr key={`hr-${i}`} style={{ border:"none", borderTop:"1px solid var(--border)", margin:"10px 0" }}/>);
      i++;
      continue;
    }

    if (/^#{1,3}\s/.test(line)) {
      numberedCounter = 0;
      const level = line.match(/^#+/)[0].length;
      elements.push(
        <p key={i} style={{ margin:"10px 0 4px", fontWeight:700, fontSize: level === 1 ? 15 : level === 2 ? 14 : 13, color:"var(--text-primary)", lineHeight:1.4 }}>
          {inlineFormat(line.replace(/^#{1,3}\s/, ""))}
        </p>
      );
      i++;
      continue;
    }

    if (line.includes("|") && line.trim().startsWith("|")) {
      numberedCounter = 0;
      const tableLines = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim().startsWith("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      const rows = tableLines
        .filter(l => !/^\s*\|[\s\-:|]+\|\s*$/.test(l))
        .map(l => l.replace(/^\||\|$/g, "").split("|").map(cell => cell.trim()));
      if (rows.length > 0) {
        const headers = rows[0];
        const body = rows.slice(1);
        elements.push(
          <div key={`tbl-${i}`} style={{ overflowX:"auto", margin:"8px 0", borderRadius:6, border:"1px solid var(--border)" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12.5 }}>
              <thead><tr>
                {headers.map((h, hi) => (
                  <th key={hi} style={{ padding:"7px 10px", textAlign:"left", fontWeight:600, background:"var(--bg-elevated, #f8fafc)", color:"var(--text-primary)", borderBottom:"2px solid var(--border)", fontSize:12 }}>
                    {inlineFormat(h)}
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {body.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td key={ci} style={{ padding:"6px 10px", borderBottom:"1px solid var(--border)", fontSize:12.5, color:"var(--text-secondary)", lineHeight:1.5 }}>
                        {inlineFormat(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }

    if (/^\d+\.\s/.test(line)) {
      if (numberedCounter === 0) numberedCounter = 1;
      const n = numberedCounter++;
      const itemText = line.replace(/^\d+\.\s/, "");
      i++;
      const subItems = [];
      while (i < lines.length && /^(\s+)?[-•*]\s/.test(lines[i])) {
        subItems.push(lines[i].replace(/^\s*[-•*]\s/, ""));
        i++;
      }
      elements.push(
        <div key={`ol-${n}-${i}`} style={{ marginBottom: subItems.length > 0 ? 10 : 4 }}>
          <div style={{ display:"flex", gap:8, alignItems:"flex-start" }}>
            <span style={{ color:"var(--blue-500)", fontWeight:600, flexShrink:0, lineHeight:1.6, fontFamily:"var(--font-mono)", fontSize:12, minWidth:16 }}>{n}.</span>
            <span style={{ lineHeight:1.6, fontWeight:500 }}>{inlineFormat(itemText)}</span>
          </div>
          {subItems.length > 0 && (
            <div style={{ marginLeft:24, marginTop:4 }}>
              {subItems.map((sub, si) => (
                <div key={si} style={{ display:"flex", gap:6, marginBottom:2, alignItems:"flex-start", fontSize:12.5, color:"var(--text-secondary)" }}>
                  <span style={{ color:"var(--text-tertiary)", flexShrink:0, lineHeight:1.6 }}>–</span>
                  <span style={{ lineHeight:1.6 }}>{inlineFormat(sub)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
      continue;
    }

    if (/^[-•*]\s/.test(line)) {
      numberedCounter = 0;
      const items = [];
      while (i < lines.length && /^[-•*]\s/.test(lines[i])) {
        items.push(
          <div key={i} style={{ display:"flex", gap:8, marginBottom:4, alignItems:"flex-start" }}>
            <span style={{ color:"var(--blue-500)", fontWeight:700, flexShrink:0, lineHeight:1.6, fontSize:14 }}>•</span>
            <span style={{ lineHeight:1.6 }}>{inlineFormat(lines[i].replace(/^[-•*]\s/, ""))}</span>
          </div>
        );
        i++;
      }
      elements.push(<div key={`ul-${i}`} style={{ margin:"4px 0" }}>{items}</div>);
      continue;
    }

    numberedCounter = 0;
    elements.push(
      <p key={i} style={{ margin:"3px 0", lineHeight:1.6, wordBreak:"break-word", overflowWrap:"anywhere" }}>
        {inlineFormat(line)}
      </p>
    );
    i++;
  }
  return elements;
}